import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Icon,
  LinearProgress,
  useTheme,
  AppBar,
  Toolbar,
  Drawer,
  IconButton,
  Divider,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Dashboard,
  People,
  Settings,
  Analytics,
  Notifications,
  History,
  Menu as MenuIcon,
  Close as CloseIcon,
  Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../contexts/AdminContext';
import { AdminGuard } from '../../components/common/AdminGuard';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));

const StatCard = styled(StyledCard)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
}));

const DRAWER_WIDTH = 280;

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, signOut } = useAuth();
  const admin = useAdmin();
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);

  const adminMenuItems = [
    {
      label: 'Dashboard',
      icon: <Dashboard />,
      path: '/admin',
      permission: 'canViewAnalytics',
    },
    {
      label: 'Users',
      icon: <People />,
      path: '/admin/users',
      permission: 'canManageUsers',
    },
    {
      label: 'Programs',
      icon: <Settings />,
      path: '/admin/programs',
      permission: 'canManagePrograms',
    },
    {
      label: 'Analytics',
      icon: <Analytics />,
      path: '/admin/analytics',
      permission: 'canViewAnalytics',
    },
    {
      label: 'Notifications',
      icon: <Notifications />,
      path: '/admin/notifications',
      permission: 'canBroadcastNotifications',
    },
    {
      label: 'Audit Logs',
      icon: <History />,
      path: '/admin/audit-logs',
      permission: 'canAccessAudit',
    },
    ...(admin.canManageRoles ? [{
      label: 'Role Management',
      icon: <Settings />,
      path: '/admin/role-management',
      permission: 'canManageRoles' as const,
    }] : []),
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Admin Panel
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {admin.userRole?.toUpperCase()}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {adminMenuItems.map((item, index) => {
          const hasPermission = admin[item.permission as keyof typeof admin];
          
          if (!hasPermission) return null;

          const isActive = location.pathname === item.path;
          
          return (
            <Box
              key={index}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setDrawerOpen(false);
              }}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 1,
                padding: 2,
                backgroundColor: isActive 
                  ? 'rgba(56, 178, 172, 0.1)' 
                  : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(56, 178, 172, 0.05)',
                },
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ color: isActive ? 'primary.main' : 'inherit', display: 'flex' }}>
                {item.icon}
              </Box>
              <Typography 
                variant="body2"
                sx={{ fontWeight: isActive ? 600 : 500 }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Divider />
      
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {user?.email}
        </Typography>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: '0.875rem',
            fontWeight: 500,
            color: theme.palette.text.primary,
            transition: 'all 0.2s',
          }}
        >
          <Logout fontSize="small" />
          Logout
        </button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Drawer */}
      {!isMobile ? (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: `1px solid ${theme.palette.divider}`,
              background: theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.95)'
                : 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(10px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              background: theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.95)'
                : 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(10px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Top Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={() => setDrawerOpen(true)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              PoinVoy Admin
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 4 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
